import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useTenantSlug } from '../../lib/use-tenant-slug';
import { useAdminOrders, useInventoryData, useDailySalesSummary } from '../../lib/admin-hooks';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { formatCurrency, formatRelativeTime, getStatusColor, getStatusLabel } from '../../lib/utils';
import { ShoppingBag, Clock, CheckCircle2, AlertTriangle, Play, Pause } from 'lucide-react';
import { Button } from '../../components/ui/Button';
export function DashboardPage() {
  const slug = useTenantSlug();
  const {
    restaurant,
    settings,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const {
    orders,
    loading: ordersLoading,
    updateOrderStatus
  } = useAdminOrders(restaurant?.id);
  const {
    summary,
    loading: salesLoading
  } = useDailySalesSummary(restaurant?.id);
  const {
    alerts,
    loading: invLoading
  } = useInventoryData(restaurant?.id);
  const [kitchenPaused, setKitchenPaused] = useState(false);
  const [togglingKitchen, setTogglingKitchen] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  useEffect(() => {
    if (settings) {
      console.log('[NOIZA][Dashboard] Settings loaded, kitchen_paused =', settings.kitchen_paused, 'restaurant_id =', restaurant?.id);
      setKitchenPaused(settings.kitchen_paused);
    }
  }, [settings, restaurant?.id]);
  const handleToggleKitchen = async () => {
    if (!restaurant) {
      console.error('[NOIZA][Dashboard] No restaurant loaded — cannot toggle');
      return;
    }
    setTogglingKitchen(true);
    setToggleError(null);
    const newValue = !kitchenPaused;
    console.log('[NOIZA][Dashboard] Toggling kitchen:', {
      restaurantId: restaurant.id,
      slug,
      currentValue: kitchenPaused,
      newValue
    });
    const {
      toggleKitchenPause
    } = await import('../../lib/kitchen-toggle');
    const result = await toggleKitchenPause(restaurant.id, newValue);
    if (result.success) {
      setKitchenPaused(result.newValue);
      setToggleError(null);
    } else {
      // Revert to DB-confirmed value
      setKitchenPaused(result.newValue);
      setToggleError(result.error || 'No se pudo actualizar el estado de cocina');
      console.error('[NOIZA][Dashboard] Toggle FAILED:', result.error);
    }
    setTogglingKitchen(false);
  };
  // Get tenant context for role-aware layout
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-2200">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-2201" />
      </div>;
  }
  const todayOrdersCount = summary?.orders_today || 0;
  const todayRevenue = summary?.total_today || 0;
  const newOrders = orders.filter(o => o.status === 'new');
  const preparingOrders = orders.filter(o => o.status === 'preparing');
  const readyOrders = orders.filter(o => o.status === 'ready');
  // Derive alert counts from row-level v_inventory_alerts (real view uses uppercase values)
  const criticalAlerts = alerts.filter(a => a.alert_level === 'CRITICAL');
  const lowAlerts = alerts.filter(a => a.alert_level === 'LOW');
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-2202">
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-id="element-2203">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-2204">
          <div data-id="element-2205">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-2206">Dashboard</h1>
            <p className="text-gray-500" data-id="element-2207">Resumen de operaciones de hoy</p>
          </div>
          <div className="flex items-center gap-3" data-id="element-2208">
            <button onClick={handleToggleKitchen} disabled={togglingKitchen} className={`flex items-center gap-2 px-4 py-2 rounded-xl border shadow-sm transition-all active:scale-[0.98] ${togglingKitchen ? 'opacity-50 pointer-events-none' : ''} ${kitchenPaused ? 'bg-red-50 border-red-200 hover:bg-red-100' : 'bg-white border-gray-200 hover:bg-gray-50'}`} data-id="element-2209">
              <span className="text-sm font-medium text-gray-600" data-id="element-2210">Cocina:</span>
              {kitchenPaused ? <Badge variant="danger" className="flex items-center gap-1" data-id="element-2211">
                  <Pause className="w-3 h-3" data-id="element-2212" /> Pausada
                </Badge> : <Badge variant="success" className="flex items-center gap-1" data-id="element-2213">
                  <Play className="w-3 h-3" data-id="element-2214" /> Activa
                </Badge>}
            </button>
          </div>
        </div>

        {/* Toggle Error */}
        {toggleError && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center justify-between" data-id="element-2215">
            <p className="text-red-700 text-sm font-medium" data-id="element-2216">⚠️ {toggleError}</p>
            <button onClick={() => setToggleError(null)} className="text-red-400 hover:text-red-600 text-xs font-bold ml-4" data-id="element-2217">
              ✕
            </button>
          </div>}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2218">
          <Card data-id="element-2219">
            <CardContent className="p-6" data-id="element-2220">
              <div className="flex items-center justify-between mb-4" data-id="element-2221">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-2222">
                  <ShoppingBag className="w-5 h-5 text-blue-600" data-id="element-2223" />
                </div>
                <span className="text-2xl font-bold text-gray-900" data-id="element-2224">
                  {todayOrdersCount}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-2225">Pedidos Hoy</p>
              <p className="text-xs text-gray-400 mt-1" data-id="element-2226">
                {formatCurrency(todayRevenue)}
              </p>
            </CardContent>
          </Card>

          <Card data-id="element-2227">
            <CardContent className="p-6" data-id="element-2228">
              <div className="flex items-center justify-between mb-4" data-id="element-2229">
                <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center" data-id="element-2230">
                  <Clock className="w-5 h-5 text-amber-600" data-id="element-2231" />
                </div>
                <span className="text-2xl font-bold text-gray-900" data-id="element-2232">
                  {newOrders.length}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-2233">
                Nuevos / Pendientes
              </p>
            </CardContent>
          </Card>

          <Card data-id="element-2234">
            <CardContent className="p-6" data-id="element-2235">
              <div className="flex items-center justify-between mb-4" data-id="element-2236">
                <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center" data-id="element-2237">
                  <ChefHatIcon className="w-5 h-5 text-purple-600" data-id="element-2238" />
                </div>
                <span className="text-2xl font-bold text-gray-900" data-id="element-2239">
                  {preparingOrders.length}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-2240">
                En Preparación
              </p>
            </CardContent>
          </Card>

          <Card data-id="element-2241">
            <CardContent className="p-6" data-id="element-2242">
              <div className="flex items-center justify-between mb-4" data-id="element-2243">
                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center" data-id="element-2244">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" data-id="element-2245" />
                </div>
                <span className="text-2xl font-bold text-gray-900" data-id="element-2246">
                  {readyOrders.length}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-500" data-id="element-2247">
                Listos para Entrega
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2248">
          {/* Recent Orders */}
          <Card className="lg:col-span-2" data-id="element-2249">
            <CardHeader data-id="element-2250">
              <CardTitle data-id="element-2251">Pedidos Recientes</CardTitle>
            </CardHeader>
            <CardContent data-id="element-2252">
              {ordersLoading ? <div className="space-y-4" data-id="element-2253">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" data-id="element-2254" />)}
                </div> : orders.length > 0 ? <div className="overflow-x-auto" data-id="element-2255">
                  <table className="w-full text-sm text-left" data-id="element-2256">
                    <thead className="text-xs text-gray-500 uppercase bg-gray-50" data-id="element-2257">
                      <tr data-id="element-2258">
                        <th className="px-4 py-3 rounded-l-lg" data-id="element-2259">Pedido</th>
                        <th className="px-4 py-3" data-id="element-2260">Cliente</th>
                        <th className="px-4 py-3" data-id="element-2261">Tipo</th>
                        <th className="px-4 py-3" data-id="element-2262">Total</th>
                        <th className="px-4 py-3" data-id="element-2263">Estado</th>
                        <th className="px-4 py-3 rounded-r-lg" data-id="element-2264">Acción</th>
                      </tr>
                    </thead>
                    <tbody data-id="element-2265">
                      {orders.slice(0, 10).map(order => <tr key={order.id} className="border-b border-gray-50 last:border-0" data-id="element-2266">
                          <td className="px-4 py-3 font-medium text-gray-900" data-id="element-2267">
                            #{order.order_number}
                            <div className="text-xs text-gray-500 font-normal" data-id="element-2268">
                              {formatRelativeTime(order.created_at)}
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-2269">{order.customer_name}</td>
                          <td className="px-4 py-3" data-id="element-2270">
                            <Badge variant="default" data-id="element-2271">
                              {order.delivery_type === 'delivery' ? 'Delivery' : order.delivery_type === 'pickup' ? 'Para llevar' : 'Mesa'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 font-medium" data-id="element-2272">
                            {formatCurrency(order.total)}
                          </td>
                          <td className="px-4 py-3" data-id="element-2273">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`} data-id="element-2274">
                              {getStatusLabel(order.status)}
                            </span>
                          </td>
                          <td className="px-4 py-3" data-id="element-2275">
                            {order.status === 'new' && <Button size="sm" onClick={() => updateOrderStatus(order.id, 'accepted')} data-id="element-2276">
                                Aceptar
                              </Button>}
                            {order.status === 'accepted' && <Button size="sm" variant="secondary" onClick={() => updateOrderStatus(order.id, 'preparing')} data-id="element-2277">
                                Preparar
                              </Button>}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div> : <div className="text-center py-8 text-gray-500" data-id="element-2278">
                  No hay pedidos recientes
                </div>}
            </CardContent>
          </Card>

          {/* Alerts & Info */}
          <div className="space-y-6" data-id="element-2279">
            <Card data-id="element-2280">
              <CardHeader data-id="element-2281">
                <CardTitle className="flex items-center gap-2" data-id="element-2282">
                  <AlertTriangle className="w-5 h-5 text-amber-500" data-id="element-2283" />
                  Alertas de Inventario
                </CardTitle>
              </CardHeader>
              <CardContent data-id="element-2284">
                {invLoading ? <Skeleton className="h-20 w-full" data-id="element-2285" /> : alerts.length > 0 ? <div className="space-y-4" data-id="element-2286">
                    {criticalAlerts.length > 0 && <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100" data-id="element-2287">
                        <span className="text-sm font-medium text-red-800" data-id="element-2288">
                          Stock Crítico
                        </span>
                        <Badge variant="danger" data-id="element-2289">{criticalAlerts.length}</Badge>
                      </div>}
                    {lowAlerts.length > 0 && <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100" data-id="element-2290">
                        <span className="text-sm font-medium text-amber-800" data-id="element-2291">
                          Stock Bajo
                        </span>
                        <Badge variant="warning" data-id="element-2292">{lowAlerts.length}</Badge>
                      </div>}
                    <div className="space-y-2 mt-2" data-id="element-2293">
                      {alerts.slice(0, 5).map(alert => <div key={alert.inventory_item_id} className="flex justify-between text-sm" data-id="element-2294">
                          <span className="text-gray-700 truncate" data-id="element-2295">
                            {alert.ingredient_name}
                          </span>
                          <span className="text-gray-500 shrink-0 ml-2" data-id="element-2296">
                            {alert.current_stock} / {alert.minimum_stock}{' '}
                            {alert.unit}
                          </span>
                        </div>)}
                    </div>
                  </div> : <p className="text-sm text-gray-500" data-id="element-2297">
                    No hay alertas de inventario.
                  </p>}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>;
}
// Helper icon
function ChefHatIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2298">
      <path d="M17 21a1 1 0 0 0 1-1v-5.35c0-.457.316-.844.727-1.041a4 4 0 0 0-2.134-7.589c-.345 0-.682.041-1.005.115-.428-1.833-2.136-3.134-4.088-3.134s-3.66 1.301-4.088 3.134c-.323-.074-.66-.115-1.005-.115a4 4 0 0 0-2.134 7.589c.411.197.727.584.727 1.041V20a1 1 0 0 0 1 1h12z" data-id="element-2299" />
      <path d="M6 17h12" data-id="element-2300" />
    </svg>;
}